import { useEffect, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import type { HubEvent } from '@aiai/shared';
import { getToken } from '../lib/token';
import { refreshAccessToken } from '../lib/api';

export interface HubSub {
  orgId: string;
}

const SOCKET_BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * Subscribes to the `/hub` socket.io namespace for the given workspace subs
 * and delivers each `hub-event` to `onEvent` (kept in a ref so a new callback
 * never tears down the connection). Events are best-effort animation triggers;
 * the server has no unsubscribe message, so unmount just disconnects. An empty
 * `subs` array means no connection.
 */
export function useHubSocket(
  subs: HubSub[],
  onEvent: (e: HubEvent) => void,
): void {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  const key = JSON.stringify(subs);

  useEffect(() => {
    const list = JSON.parse(key) as HubSub[];
    if (list.length === 0) return;
    let disposed = false;

    const socket: Socket = io(`${SOCKET_BASE}/hub`, {
      path: '/socket.io',
      transports: ['websocket'],
      auth: { token: getToken() },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 8000,
    });

    socket.on('connect', () => {
      // Re-sent on every (re)connect: the server forgets subs with the socket.
      for (const sub of list) {
        socket.emit('subscribe', sub, (ack?: { ok?: boolean }) => {
          if (ack && ack.ok === false) console.warn('hub subscribe rejected', sub);
        });
      }
    });
    socket.on('disconnect', (reason: string) => {
      // Expired/invalid token: the server force-disconnects and socket.io never
      // auto-retries that, so refresh silently and reconnect.
      if (reason === 'io server disconnect') {
        refreshAccessToken().then((newToken) => {
          if (disposed || !newToken) return;
          socket.auth = { token: newToken };
          socket.connect();
        });
      }
    });
    socket.on('hub-event', (event: HubEvent) => onEventRef.current(event));

    return () => {
      disposed = true;
      socket.removeAllListeners();
      socket.disconnect();
    };
  }, [key]);
}
